/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import type { APP_SCOPE } from '@nx/constants';
import { type LDFlagSetRaw } from '@nx/launch-darkly-service';
import * as SegmentJS from '@segment/analytics-next';
import * as Sentry from '@sentry/react';
import { isEqual } from 'lodash-es';
import mixpanel from 'mixpanel-browser';

import type { AnalyticsAdapter, AnalyticsTrackPayload } from './analytics-adapter';

// TODO: Remove once Explore is migrated away from `window.analytics`
declare global {
  interface Window {
    analytics: SegmentJS.AnalyticsBrowser;
    mixpanel: typeof mixpanel;
  }
}

const PREFIX_BY_SCOPE: { [key in APP_SCOPE]: string } = {
  query: 'QRY',
  import: 'IMP',
  explore: 'BL',
  debugger: 'EXA',
  ops: 'OPS',
  framework: 'CON',
  guides: 'CON',
  aura: 'CON',
  test: 'TEST',
  desktop: 'DESK',
  dashboards: 'DSH',
  dataApi: 'DAPI',
  conversations: 'CONV',
  microsoftFabric: 'MSFAB',
};

export class SegmentNotFoundError extends Error {
  constructor() {
    super('Segment instance is unavailable');
    this.name = 'SegmentNotFoundError';
  }
}

export type SegmentAnalyticsAdapterConfig = {
  /** Segment API key */
  apiKey: string;
  /**
   * Event prefix override; ignores "scope" passed as part of tracked event
   *
   * Useful for standalone apps.
   */
  eventPrefix?: string;
  /**
   * Mixpanel configuration for session replay
   */
  mixpanel?: {
    /** Mixpanel project token */
    projectToken: string;
    /** Session recording sample rate (1-100) */
    recordSessionsPercent?: number;
    /** Use simplified ID merge (defaults to true) */
    useSimplifiedIdMerge?: boolean;
    /** Session replay configuration */
    sessionReplay?: {
      /** CSS selector for elements to mask text */
      maskTextSelector?: string;
      /** CSS class for elements to mask text */
      maskTextClass?: string;
      /** CSS selector for elements to block from recording */
      blockSelector?: string;
      /** CSS class for elements to block from recording */
      blockClass?: string;
    };
  };
};

/**
 * Analytics adapter for Segment Analytics.js
 */
export class SegmentAnalyticsAdapter<Event extends string> implements AnalyticsAdapter<Event> {
  private analytics: SegmentJS.AnalyticsBrowser;

  private plugins: SegmentJS.Plugin[] = [];

  constructor(private config: SegmentAnalyticsAdapterConfig) {
    const analytics = new SegmentJS.AnalyticsBrowser();
    this.analytics = analytics;
    window.analytics = analytics;

    // Initialize Mixpanel if configured
    if (this.config.mixpanel) {
      mixpanel.init(this.config.mixpanel.projectToken, {
        record_sessions_percent: this.config.mixpanel.recordSessionsPercent ?? 5,
        record_mask_text_selector: this.config.mixpanel.sessionReplay?.maskTextSelector ?? '',
        record_mask_text_class: this.config.mixpanel.sessionReplay?.maskTextClass ?? '',
        record_block_selector: this.config.mixpanel.sessionReplay?.blockSelector ?? '',
        record_block_class: this.config.mixpanel.sessionReplay?.blockClass ?? '',
      });
    }
  }

  async init() {
    this.analytics.load({ writeKey: this.config.apiKey });

    // Add Mixpanel session replay middleware if configured
    if (this.config.mixpanel) {
      void this.analytics.addSourceMiddleware(({ payload, next }) => {
        // For debugging purposes, call this.debugMixpanelSessionReplay(payload);

        if (payload.obj.type === 'identify') {
          if (payload.obj.userId) {
            mixpanel.identify(payload.obj.userId);
          }
        }

        next(payload);
      });
    }

    if (this.plugins.length > 0) {
      await this.ready();
      await this.analytics.register(...this.plugins);
    }
  }

  async identify({ userId, orgId, flags }: { userId: string; orgId: string | null; flags: LDFlagSetRaw | null }) {
    await this.ready();

    const user = await this.analytics.user();
    const existingId = user.id();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const existingOrgId = user.traits()?.orgId;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const existingFlags = user.traits()?.flags;

    if (userId !== existingId || (orgId !== existingOrgId && orgId !== null)) {
      await this.analytics.identify(userId, { orgId, flags: flags ?? {} });

      // We also set the user ID in Sentry
      Sentry.setUser({ id: userId });
    } else if (!isEqual(flags, existingFlags)) {
      await this.analytics.identify(userId, { orgId, flags: flags ?? {} });
    }
  }

  async trackNavigation() {
    await this.ready();

    await this.analytics.page();
  }

  async trackEvent(payload: AnalyticsTrackPayload<Event, SegmentJS.EventProperties>) {
    await this.ready();

    const eventPrefix = this.config.eventPrefix ?? PREFIX_BY_SCOPE[payload.scope];
    const eventName = payload.event
      // Convert someName to some_Name
      .replace(/[a-z][A-Z]/g, (letters: string): string => `${letters[0] ?? ''}_${letters[1] ?? ''}`)
      .toUpperCase();

    await this.analytics.track(`${eventPrefix}_${eventName}`, payload.properties);
  }

  addPlugin(plugin: SegmentJS.Plugin) {
    this.plugins.push(plugin);
  }

  /**
   * Get the current Mixpanel session replay URL for debugging purposes.
   */
  // getSessionReplayUrl(): string | null {
  //   if (!this.config.mixpanel) {
  //     return null;
  //   }

  //   return window.mixpanel.get_session_replay_url();
  // }

  /**
   * Debug helper for Mixpanel session replay properties and URL.
   */
  // private debugMixpanelSessionReplay(payload: any) {
  //   if (payload.obj.type === 'track' || payload.obj.type === 'page') {
  //     if (window.mixpanel) {
  //       const sessionReplayProperties = mixpanel.get_session_recording_properties();
  //       console.log('Session replay properties:', sessionReplayProperties);
  //       // Session replay URL for debugging
  //       const sessionReplayUrl = (window.mixpanel as any).get_session_replay_url?.();
  //       if (sessionReplayUrl) {
  //         console.log('Mixpanel Replay URL:', sessionReplayUrl);
  //       }
  //       payload.obj.properties = {
  //         ...payload.obj.properties,
  //         ...sessionReplayProperties,
  //       };
  //     }
  //   }
  // }
  private async ready() {
    await this.analytics.ready();
  }
}
